import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      
      // Fetch permissions if user is staff
      if (['admin', 'moderator', 'editor'].includes(res.data.role)) {
        try {
          const permRes = await axios.get(`${API}/admin/permissions`);
          setPermissions(permRes.data);
        } catch {
          setPermissions(null);
        }
      }
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    // Fetch permissions if user is staff
    if (['admin', 'moderator', 'editor'].includes(userData.role)) {
      try {
        const permRes = await axios.get(`${API}/admin/permissions`);
        setPermissions(permRes.data);
      } catch {
        setPermissions(null);
      }
    }
    
    return userData;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissions(null);
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!permissions) return false;
    return permissions.permissions?.[permission] === true;
  };

  // Check if user is staff (admin, moderator, or editor)
  const isStaff = user?.role && ['admin', 'moderator', 'editor'].includes(user.role);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin: user?.role === 'admin',
      isStaff,
      permissions,
      hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
